import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getHotFilms, searchFilms } from "@/utils/film";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentMeta(
    query ? `妖精网站 - “${query}”搜索结果` : "妖精网站 - 搜索妖精影视",
    "在妖精网站搜索妖精题材的电影、电视剧作品，快速获取剧情介绍与相关推荐。"
  );

  const results = useMemo(() => searchFilms(query), [query]);
  const hotFilms = useMemo(() => getHotFilms(5), []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
        搜索妖精影视
      </h1>
      <p className="mt-2 text-sm text-foreground-500">
        输入影视名称、导演、主演或标签，快速定位你想看的作品
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center sm:gap-2"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md bg-background-100 px-3 py-2">
          <i className="ri-search-2-line text-lg text-primary-500" />
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索妖精影视作品"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {query ? (
        <p className="mt-6 text-sm text-foreground-600">
          关键词「<span className="font-semibold text-primary-600">{query}</span>」 共匹配到{" "}
          <span className="font-semibold text-primary-600">{results.length}</span> 部妖精影视作品
        </p>
      ) : (
        <p className="mt-6 text-sm text-foreground-500">请输入关键词开始搜索</p>
      )}

      {query && results.length === 0 ? (
        <div className="mt-8">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <p className="font-heading text-lg font-semibold text-foreground-800">
              未找到符合条件的内容
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或看看下面这些热门妖精影视
            </p>
          </div>
          <div className="mt-10">
            <SectionHeading title="热门妖精影视推荐" icon="ri-fire-line" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {hotFilms.map((film) => (
                <FilmCard key={film.id} film={film} showIntro={false} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        query && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {results.map((film) => (
              <FilmCard key={film.id} film={film} showPopularity showIntro={false} />
            ))}
          </div>
        )
      )}
    </div>
  );
}